// Чистые помощники слоя позиций спутников на карте (раздел «Спутники»).
// Здесь — только тестируемая логика без MapLibre: цвета/подписи категорий,
// data-driven выражение icon-image, сборка GeoJSON из позиций и элементы легенды.
// Отрисовка пиктограмм и подключение слоёв живут в SatellitesPage.tsx.

import type maplibregl from 'maplibre-gl'
import type { SatellitePosition } from '../api'
import { t } from '../i18n'

export interface CategoryMeta {
  /** Собственное имя созвездия — не переводится (ORBCOMM, Spire, SITRO-AIS). */
  name?: string
  /** Ключ каталога для описательных категорий («Оптика», «Без категории»). */
  labelKey?: string
  color: string
}

// Категории реестра (см. фазы 1–2 бэкенда) → человекочитаемая подпись + цвет.
// Цвета различимы и читаются на светлой carto-подложке в обеих темах кабинета.
// unknown обязателен: у части КА категория ещё не размечена куратором.
export const CATEGORY_META: Record<string, CategoryMeta> = {
  // Имена созвездий — собственные, они не переводятся ни на один язык.
  orbcomm: { name: 'ORBCOMM', color: '#f59e0b' },
  spire: { name: 'Spire', color: '#06b6d4' },
  'iridium-next': { name: 'Iridium-NEXT', color: '#8b5cf6' },
  exactearth: { name: 'exactEarth', color: '#10b981' },
  sitro: { name: 'SITRO-AIS', color: '#ef4444' },
  'ais-smallsat': { labelKey: 'satellites.category.aisSmallsat', color: '#3b82f6' },
  // Задел на расширение реестра (Фаза 6b): радар/оптика для детекции «молчащих»
  // судов. Спутников пока нет в seed'е, но админ-курация уже может их разметить.
  sar: { labelKey: 'satellites.category.sar', color: '#ec4899' },
  optical: { labelKey: 'satellites.category.optical', color: '#84cc16' },
  unknown: { labelKey: 'satellites.category.unknown', color: '#94a3b8' },
}

// Префикс id пиктограммы категории. Образы регистрируются как `sat-ic-<category>`
// (см. SatellitesPage), а слой выбирает нужный через satIconExpr().
export const SAT_ICON_PREFIX = 'sat-ic-'

/** data-driven icon-image: `sat-ic-` + категория фичи (unknown, если её нет).
 *  coalesce страхует фичи без свойства category — иначе concat дал бы `sat-ic-`
 *  без образа, и маркер пропал бы. Каст — как у прочих выражений в MapCanvas. */
export function satIconExpr(): maplibregl.ExpressionSpecification {
  return ['concat', SAT_ICON_PREFIX, ['coalesce', ['get', 'category'], 'unknown']] as unknown as maplibregl.ExpressionSpecification
}

/** Цвет категории; неизвестная/пустая → цвет `unknown`. */
export function categoryColor(category: string): string {
  return CATEGORY_META[category]?.color ?? CATEGORY_META.unknown.color
}

/** Подпись категории; неизвестная → сама строка категории (не теряем данные).
 *
 *  Имя созвездия (`name`) — собственное и одинаково на всех языках; всё
 *  остальное («Оптика», «Без категории») живёт в каталоге как `labelKey`. */
export function categoryLabel(category: string): string {
  const meta = CATEGORY_META[category]
  if (!meta) return category
  return meta.name ?? (meta.labelKey ? t(meta.labelKey) : category)
}

/** Позиции спутников → FeatureCollection точек [lon, lat] со свойствами карточки.
 *  Спутники с нечисловыми координатами пропускаем (пропагация TLE могла не сойтись
 *  — иначе MapLibre получил бы NaN и уронил бы всю коллекцию). */
export function positionsToGeoJSON(sats: SatellitePosition[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const s of sats) {
    if (!Number.isFinite(s.lat) || !Number.isFinite(s.lon)) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
      properties: {
        norad_id: s.norad_id,
        name: s.name,
        category: s.category,
        operator: s.operator,
        orbit_class: s.orbit_class,
        status: s.status,
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export interface CategoryLegendItem {
  category: string
  label: string
  color: string
  count: number
}

/** Элементы легенды по карте счётчиков «категория → число». Только присутствующие
 *  (count > 0) категории, отсортированные по убыванию числа спутников. Принимаем
 *  готовую карту счётчиков (а не позиции) — чтобы функция оставалась чистой. */
export function categoryLegendItems(counts: Record<string, number>): CategoryLegendItem[] {
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([category, count]) => ({
      category,
      label: categoryLabel(category),
      color: categoryColor(category),
      count,
    }))
    .sort((a, b) => b.count - a.count)
}
